import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import SOCKET_EVENTS from "./events";
import { connectSocket, getSocket } from "./socketClient";

/**
 * Everything the chat UI needs from the realtime layer.
 *
 * The hook owns the transient realtime state (who is online, who is typing,
 * unread badges, connection health) and forwards the message stream to the
 * caller through callbacks, so the page keeps owning its own message list.
 *
 * Usage:
 *   const rt = useChatSocket({
 *     activeChatId: chatId,
 *     onNewMessage: (payload) => ...,
 *     onMessageMutated: (payload, kind) => ...,
 *     onMessagesCleared: (payload) => ...,
 *     onStatusChange: (payload) => ...,
 *     onChatListChanged: (payload, kind) => ...,
 *   });
 */
const useChatSocket = ({
  activeChatId = null,
  onNewMessage,
  onMessageAck,
  onMessageMutated,
  onMessagesCleared,
  onStatusChange,
  onSeen,
  onChatListChanged,
} = {}) => {
  const [connected, setConnected] = useState(false);
  const [onlineUsers, setOnlineUsers] = useState({}); // userId -> { online, lastSeen }
  const [typingByChat, setTypingByChat] = useState({}); // chatId -> { userId: user }
  const [unread, setUnread] = useState({ total: 0, byChat: {} });

  const currentUserId = window.sessionStorage.getItem("user_id");

  // Callbacks live in refs so the socket listeners are attached exactly once.
  const handlers = useRef({});
  handlers.current = {
    onNewMessage,
    onMessageAck,
    onMessageMutated,
    onMessagesCleared,
    onStatusChange,
    onSeen,
    onChatListChanged,
  };

  const typingTimers = useRef({}); // chatId -> timeout for our own "stop typing"
  const activeChatRef = useRef(activeChatId);
  activeChatRef.current = activeChatId;

  const mergePresence = useCallback((entries) => {
    setOnlineUsers((previous) => {
      const next = { ...previous };
      entries.forEach(({ userId, online, lastSeen }) => {
        next[String(userId)] = { online: Boolean(online), lastSeen: lastSeen ?? null };
      });
      return next;
    });
  }, []);

  // -- connection + listeners ------------------------------------------------
  useEffect(() => {
    const socket = connectSocket();
    if (!socket) return undefined;

    const call = (name, ...args) => {
      const fn = handlers.current[name];
      if (typeof fn === "function") fn(...args);
    };

    const onConnect = () => setConnected(true);
    const onDisconnect = () => {
      setConnected(false);
      setTypingByChat({});
    };

    const onReady = (payload) => {
      setConnected(true);
      if (payload?.unread) setUnread(payload.unread);
      if (Array.isArray(payload?.onlineUsers)) mergePresence(payload.onlineUsers);
    };

    const onPresenceList = (payload) => mergePresence(payload?.users || []);
    const onPresenceOnline = (payload) =>
      mergePresence([{ ...payload, online: true }]);
    const onPresenceOffline = (payload) =>
      mergePresence([{ ...payload, online: false }]);

    const onIncoming = (payload) => {
      call("onNewMessage", payload);

      // Someone else's message in the chat we are staring at is read instantly.
      const senderId = String(payload?.message?.sender?._id || payload?.message?.sender || "");
      if (
        String(payload?.chatId) === String(activeChatRef.current) &&
        senderId !== String(currentUserId)
      ) {
        socket.emit(SOCKET_EVENTS.MESSAGE_SEEN, { chatId: payload.chatId });
      }
    };

    const onTypingStart = (payload) => {
      if (String(payload?.userId) === String(currentUserId)) return;
      setTypingByChat((previous) => ({
        ...previous,
        [payload.chatId]: {
          ...(previous[payload.chatId] || {}),
          [payload.userId]: payload.user || { _id: payload.userId },
        },
      }));
    };

    const onTypingStop = (payload) => {
      setTypingByChat((previous) => {
        const chat = { ...(previous[payload.chatId] || {}) };
        delete chat[payload.userId];
        return { ...previous, [payload.chatId]: chat };
      });
    };

    const onUnread = (payload) => {
      if (!payload) return;
      setUnread((previous) => ({
        total: payload.total ?? previous.total,
        byChat: payload.byChat ? payload.byChat : previous.byChat,
      }));
    };

    const listeners = [
      ["connect", onConnect],
      ["disconnect", onDisconnect],
      [SOCKET_EVENTS.CONNECTED, onReady],
      [SOCKET_EVENTS.PRESENCE_LIST, onPresenceList],
      [SOCKET_EVENTS.PRESENCE_ONLINE, onPresenceOnline],
      [SOCKET_EVENTS.PRESENCE_OFFLINE, onPresenceOffline],
      [SOCKET_EVENTS.MESSAGE_NEW, onIncoming],
      [SOCKET_EVENTS.MESSAGE_ACK, (payload) => call("onMessageAck", payload)],
      [
        SOCKET_EVENTS.MESSAGE_EDITED,
        (payload) => call("onMessageMutated", payload, "edited"),
      ],
      [
        SOCKET_EVENTS.MESSAGE_DELETED,
        (payload) => call("onMessageMutated", payload, "deleted"),
      ],
      [
        SOCKET_EVENTS.MESSAGES_CLEARED,
        (payload) => call("onMessagesCleared", payload),
      ],
      [SOCKET_EVENTS.MESSAGE_STATUS, (payload) => call("onStatusChange", payload)],
      [SOCKET_EVENTS.MESSAGE_SEEN, (payload) => call("onSeen", payload)],
      [
        SOCKET_EVENTS.MESSAGE_DELIVERED,
        (payload) => call("onSeen", { ...payload, delivered: true }),
      ],
      [SOCKET_EVENTS.TYPING_START, onTypingStart],
      [SOCKET_EVENTS.TYPING_STOP, onTypingStop],
      [SOCKET_EVENTS.UNREAD_UPDATE, onUnread],
      [
        SOCKET_EVENTS.CHAT_CREATED,
        (payload) => call("onChatListChanged", payload, "created"),
      ],
      [
        SOCKET_EVENTS.CHAT_UPDATED,
        (payload) => call("onChatListChanged", payload, "updated"),
      ],
      [
        SOCKET_EVENTS.CHAT_DELETED,
        (payload) => call("onChatListChanged", payload, "deleted"),
      ],
      [
        SOCKET_EVENTS.CHAT_PINNED,
        (payload) => call("onChatListChanged", payload, "pinned"),
      ],
      [
        SOCKET_EVENTS.ERROR,
        (payload) => console.warn("[socket] server error:", payload),
      ],
    ];

    listeners.forEach(([event, handler]) => socket.on(event, handler));
    if (socket.connected) setConnected(true);

    return () => {
      listeners.forEach(([event, handler]) => socket.off(event, handler));
    };
  }, [currentUserId, mergePresence]);

  // -- join / leave the active conversation ---------------------------------
  useEffect(() => {
    const socket = getSocket();
    if (!socket || !activeChatId || !connected) return undefined;

    socket.emit(SOCKET_EVENTS.CHAT_JOIN, { chatId: activeChatId });

    return () => {
      socket.emit(SOCKET_EVENTS.CHAT_LEAVE, { chatId: activeChatId });
    };
  }, [activeChatId, connected]);

  // -- actions ---------------------------------------------------------------
  const sendMessage = useCallback(
    (payload) =>
      new Promise((resolve, reject) => {
        const socket = getSocket();
        if (!socket?.connected) {
          reject(new Error("offline"));
          return;
        }

        socket.emit(SOCKET_EVENTS.MESSAGE_SEND, payload, (response) => {
          if (response?.ok) resolve(response.message);
          else reject(new Error(response?.message || "send failed"));
        });
      }),
    []
  );

  /**
   * Called on every keystroke. Emits `typing:start` once and rearms a timer
   * that emits `typing:stop` after a short idle period.
   */
  const notifyTyping = useCallback((chatId) => {
    const socket = getSocket();
    if (!socket?.connected || !chatId) return;

    if (!typingTimers.current[chatId]) {
      socket.emit(SOCKET_EVENTS.TYPING_START, { chatId });
    }

    clearTimeout(typingTimers.current[chatId]);
    typingTimers.current[chatId] = setTimeout(() => {
      delete typingTimers.current[chatId];
      socket.emit(SOCKET_EVENTS.TYPING_STOP, { chatId });
    }, 2500);
  }, []);

  const stopTyping = useCallback((chatId) => {
    const socket = getSocket();
    if (!chatId) return;

    clearTimeout(typingTimers.current[chatId]);
    delete typingTimers.current[chatId];
    socket?.emit(SOCKET_EVENTS.TYPING_STOP, { chatId });
  }, []);

  const markSeen = useCallback((chatId, messageIds) => {
    getSocket()?.emit(SOCKET_EVENTS.MESSAGE_SEEN, { chatId, messageIds });
  }, []);

  const markDelivered = useCallback((chatId, messageIds) => {
    getSocket()?.emit(SOCKET_EVENTS.MESSAGE_DELIVERED, { chatId, messageIds });
  }, []);

  const refreshUnread = useCallback(() => {
    getSocket()?.emit(SOCKET_EVENTS.UNREAD_SYNC);
  }, []);

  const refreshPresence = useCallback((userIds) => {
    getSocket()?.emit(SOCKET_EVENTS.PRESENCE_GET, { userIds });
  }, []);

  // Clear our own typing timers when the hook unmounts.
  useEffect(
    () => () => {
      Object.values(typingTimers.current).forEach(clearTimeout);
      typingTimers.current = {};
    },
    []
  );

  const isUserOnline = useCallback(
    (userId) => Boolean(onlineUsers[String(userId)]?.online),
    [onlineUsers]
  );

  const lastSeenOf = useCallback(
    (userId) => onlineUsers[String(userId)]?.lastSeen || null,
    [onlineUsers]
  );

  const typingUsersInActiveChat = useMemo(
    () => Object.values(typingByChat[activeChatId] || {}),
    [typingByChat, activeChatId]
  );

  return {
    connected,
    onlineUsers,
    isUserOnline,
    lastSeenOf,
    typingByChat,
    typingUsers: typingUsersInActiveChat,
    unread,
    unreadFor: (chatId) => unread.byChat?.[String(chatId)] || 0,
    sendMessage,
    notifyTyping,
    stopTyping,
    markSeen,
    markDelivered,
    refreshUnread,
    refreshPresence,
  };
};

export default useChatSocket;
